import React, { ReactNode } from 'react';
import { ASSET_PREFIX } from 'utils/constants';
import NextWinnerBanner from 'components/NextWinnerBanner';
import { Parallax } from 'react-parallax';
import Script from 'next/script';

interface IVideoOptions { 
    height: string;
    width: string;
    poster: string;
    title: string;
    vimeoId: string;
}
interface IVideoProps {
  portrait: IVideoOptions;
  landscape: IVideoOptions;
}

interface IHeightAtBreakpointProps { 
  min360px?: string
  min500px?: string
  min600px?: string
  min700px?: string
  min1300px?: string
  min1400px?: string
  min1500px?: string
  min1600px?: string
  min1800px?: string
}

interface IHeroSectionProps {
  id?: string;
  heroUrl?: string;
  videoProps?: IVideoProps;
  heroAltText?: string;
  dimBackgroundOpacity?: number;
  overlapMoreWinnersBanner?: boolean;
  heroPosition?: string;
  showUpcomingDrawDate?: boolean;
  children?: ReactNode;
  height?: string;
  largerHeight?: string;
  wideDesktopHeight?: string;
  widestDesktopHeight?: string;
  heightAtBreakpoint?: IHeightAtBreakpointProps;
  glowBarColor?: string;
  bottomBar?: ReactNode;
  marginBottom?: string;
  dailyDraw?: boolean;
  raffle?: boolean;
  leaderboard?: boolean;
  padded?: boolean;
  disableParallax?: boolean;
  style?: React.CSSProperties;
}

const HeroSection = ({
  id = "heroSectionContainer",
  heroUrl = `${ASSET_PREFIX}/images/hero-new.jpg`,
  videoProps = undefined,
  heroAltText,
  dimBackgroundOpacity = 1.0,
  overlapMoreWinnersBanner = false,
  heroPosition = "center",
  showUpcomingDrawDate,
  children,
  height = "400px",
  largerHeight,
  wideDesktopHeight,
  widestDesktopHeight,
  heightAtBreakpoint = {},
  glowBarColor = "#D7FBF4",
  bottomBar,
  marginBottom = "20px",
  dailyDraw = false,
  raffle = false,
  leaderboard = false,
  padded = true,
  disableParallax = false,
  style,
}: IHeroSectionProps) => {
  let headingElement: ReactNode;
  if (dailyDraw) {
    headingElement = (
      <>
        NEXT <strong>DRAW</strong> IN
      </>
    );
  } else if (raffle) {
    headingElement = (
      <>
        Guaranteed <strong>Winner</strong> In
      </>
    );
  } else if (leaderboard) {
    headingElement = (
      <>
        NEXT <strong>LEADERBOARD</strong> DRAW
      </>
    );
  } else {
    headingElement = (
      <>
        MORE <strong>WINNERS</strong> IN
      </>
    );
  }

  // Only use parallax scrolling on desktop
  const useParallaxScrollingBackground =
    !disableParallax &&
    typeof window !== "undefined" &&
    !window.matchMedia("(hover: none) and (pointer: coarse)").matches;

  const getHeroContainer = () => (
    <div className="heroContainer" style={style}>
      {dimBackgroundOpacity > 0 && (
        <div
          style={{
            position: "absolute",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.7) 100%)",
            width: "100%",
            bottom: 0,
            top: 0,
            left: 0,
            right: 0,
            opacity: dimBackgroundOpacity,
          }}
        />
      )}

      {!showUpcomingDrawDate /* `false` indicates that we should not display the banner */ && (
        <NextWinnerBanner
          barTint={glowBarColor}
          showUpcomingDrawDate={showUpcomingDrawDate}
          heading={headingElement}
          showDays={!dailyDraw}
          style={{
            position: overlapMoreWinnersBanner ? "absolute" : "relative",
            marginTop: "-1px",
          }}
        />
      )}

      <div
        className={padded ? "paddedMaxWidthContainer" : ""}
        style={{
          textAlign: "center",
          margin: "auto",
          marginBottom: overlapMoreWinnersBanner ? 0 : "auto",
          zIndex: 1,
          width: "100%",
        }}
      >
        {children}
      </div>

      {bottomBar}

      <style jsx>
        {`
          .heroContainer {
            width: 100%;
            height: ${height};
            color: white;
            text-align: center;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            position: relative;
            z-index: 0;
            transition: 0.3s;
          }

          @media (min-width: 360px) {
            .heroContainer {
              ${heightAtBreakpoint.min360px &&
              `height: ${heightAtBreakpoint.min360px};`}
            }
          }

          @media (min-width: 500px) {
            .heroContainer {
              ${heightAtBreakpoint.min500px &&
              `height: ${heightAtBreakpoint.min500px};`}
            }
          }

          @media (min-width: 600px) {
            .heroContainer {
              ${heightAtBreakpoint.min600px &&
              `height: ${heightAtBreakpoint.min600px};`}
            }
          }

          @media (min-width: 700px) {
            .heroContainer {
              ${heightAtBreakpoint.min700px &&
              `height: ${heightAtBreakpoint.min700px};`}
            }
          }

          @media (min-width: 900px) {
            .heroContainer {
              ${largerHeight && `height: ${largerHeight};`}
            }
          }

          @media (min-width: 1200px) {
            .heroContainer {
              ${wideDesktopHeight && `height: ${wideDesktopHeight};`}
            }
          }

          @media (min-width: 1300px) {
            .heroContainer {
              ${heightAtBreakpoint.min1300px &&
              `height: ${heightAtBreakpoint.min1300px};`}
            }
          }

          @media (min-width: 1400px) {
            .heroContainer {
              ${heightAtBreakpoint.min1400px &&
              `height: ${heightAtBreakpoint.min1400px};`}
            }
          }

          @media (min-width: 1500px) {
            .heroContainer {
              ${heightAtBreakpoint.min1500px &&
              `height: ${heightAtBreakpoint.min1500px};`}
            }
          }

          @media (min-width: 1600px) {
            .heroContainer {
              ${heightAtBreakpoint.min1600px &&
              `height: ${heightAtBreakpoint.min1600px};`}
            }
          }

          @media (min-width: 1800px) {
            .heroContainer {
              ${heightAtBreakpoint.min1800px &&
              `height: ${heightAtBreakpoint.min1800px};`}
              ${widestDesktopHeight && `height: ${widestDesktopHeight};`}
            }
          }
        `}
      </style>
    </div>
  );

  return (
    <>
      {videoProps ? (
        <Parallax
          style={{
            width: "100%",
            margin: 0,
            backgroundSize: "cover",
            backgroundPosition: heroPosition,
            marginBottom,
          }}
        >
          <div className="videoBlock">
            <div className="videoContainer">
              <img
                className="transparentImgForSizing"
                src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              />
              <iframe
                className="videoPortrait"
                src={`https://player.vimeo.com/video/${videoProps.portrait.vimeoId}?background=1&amp;playsinline=1&amp;autoplay=1&amp;loop=1&amp;badge=0&amp;autopause=1&amp;player_id=0&amp;app_id=58479;`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={
                  videoProps.portrait.title ??
                  `portrait-hero-${videoProps.portrait.vimeoId}`
                }
              />
              <iframe
                className="videoLandscape"
                src={`https://player.vimeo.com/video/${videoProps.landscape.vimeoId}?background=1&amp;playsinline=1&amp;autoplay=1&amp;loop=1&amp;badge=0&amp;autopause=1&amp;player_id=0&amp;app_id=58479;`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={
                  videoProps.landscape.title ??
                  `landscape-hero-${videoProps.landscape.vimeoId}`
                }
              />
            </div>
            <Script id="vimeo" src="https://player.vimeo.com/api/player.js" />
          </div>
          {getHeroContainer()}
        </Parallax>
      ) : (
        <Parallax
          bgClassName={`${id}-parallaxImage`}
          bgImage={heroUrl}
          bgImageAlt={heroAltText}
          strength={useParallaxScrollingBackground ? 500 : 0}
          style={{
            width: "100%",
            margin: 0,
            backgroundSize: "cover",
            backgroundPosition: heroPosition,
            marginBottom,
          }}
        >
          {getHeroContainer()}
        </Parallax>
      )}

      <style jsx>
        {`
          .videoBlock {
            position: absolute;
            top: 0;
            left: 0;
            min-height: 100%;
            min-width: 100%;
            background-image: url(${videoProps &&
            videoProps.portrait &&
            videoProps.portrait.poster});
            background-position: center;
            background-size: cover;
          }

          .videoContainer {
            position: relative;
            display: inline-block;
            min-width: 100%;
            min-height: 100%;
          }

          .transparentImgForSizing {
            width: calc(
              ${videoProps ? videoProps.portrait.height : 0} /
                ${videoProps ? videoProps.portrait.width : 1} * 100%
            );
          }

          .videoPortrait,
          .videoLandscape {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            min-width: 100%;
            min-height: 100%;
            margin-top: -5px;
          }

          .videoLandscape {
            display: none;
          }

          @media (min-width: 1260px) {
            .videoBlock {
              background-image: url(${videoProps &&
              videoProps.landscape &&
              videoProps.landscape.poster});
            }

            .transparentImgForSizing {
              width: calc(
                ${videoProps ? videoProps.landscape.height : 0} /
                  ${videoProps ? videoProps.landscape.width : 1} * 100%
              );
            }

            .videoPortrait {
              display: none;
            }

            .videoLandscape {
              display: unset;
              height: 100%;
            }
          }

          :global(.${id}-parallaxImage) {
            width: 100% !important;
            object-fit: cover !important;
            max-height: max(
              500px,
              calc(${wideDesktopHeight ?? "0px"} + 200px),
              calc(${widestDesktopHeight ?? "0px"} + 200px)
            );
          }
        `}
      </style>
    </>
  );
};

export default HeroSection;
